import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Controller, useForm } from 'react-hook-form'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Input as InputComponent } from '@/components/ui/input'
import { Textarea as TextareaComponent } from '@/components/ui/textarea'
import { Label as LabelComponent } from '@/components/ui/label'
import BrandFilesField, {
    BRAND_FILES_SLOT,
    FileSlot,
    LOGO_SLOT,
    LicenseBrandFile,
    filesError,
} from '@/components/campaigns/BrandFilesField'
import { UPDATE_LICENSE_OVERVIEW } from '@/graphql/ads/prompt-suggestion-queries'
import { ButtonProps } from '@/types'

// Type casts for the UI components from JSX files
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>
const Input = InputComponent as unknown as React.FC<React.InputHTMLAttributes<HTMLInputElement>>
const Textarea = TextareaComponent as React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>>
const Label = LabelComponent as React.FC<React.LabelHTMLAttributes<HTMLLabelElement>>

export const OVERVIEW_MAX_LENGTH = 3000

export interface LicenseOverview {
    primaryUrl: string | null
    overview: string | null
    socialUrls: string[]
    styleGuideUrl: string | null
    brandFiles: LicenseBrandFile[]
    logo: LicenseBrandFile | null
    brandProfileStatus: string | null
    complete: boolean
}

interface FormValues {
    link: string
    overview: string
    styleGuideUrl: string
}

/** Hosts of social platforms: a link on one is a social profile, never the website. */
const SOCIAL_HOSTS = [
    'instagram.com',
    'instagr.am',
    'youtube.com',
    'youtu.be',
    'facebook.com',
    'fb.com',
    'fb.me',
    'tiktok.com',
    'x.com',
    'twitter.com',
    'linkedin.com',
    'pinterest.com',
    'pin.it',
    'threads.net',
    'threads.com',
]

const FIELD_NAMES = ['overview', 'styleGuideUrl']

const LINK_FIELDS = ['links', 'primaryUrl', 'socialUrls']

const LINK_HINT_ID = 'license-overview-link-hint'
const LINK_ERROR_ID = 'license-overview-link-error'

type LinkKind = 'primaryUrl' | 'socialUrls'

type OverviewInput = Pick<LicenseOverview, 'primaryUrl' | 'socialUrls' | 'overview' | 'styleGuideUrl'>

/**
 * True when a complete saved overview, whose link was read, has exactly these values and brand files, so saving
 * again would change nothing. A failed link read is saved again, which retries reading it.
 */
const isUnchanged = (
    input: OverviewInput,
    filesChanged: boolean,
    saved?: Partial<LicenseOverview> | null,
): saved is LicenseOverview =>
    !filesChanged &&
    Boolean(saved?.complete) &&
    saved?.brandProfileStatus === 'ready' &&
    (Object.keys(input) as (keyof OverviewInput)[]).every((field) =>
        field === 'socialUrls'
            ? input.socialUrls.join('\n') === (saved?.socialUrls ?? []).join('\n')
            : input[field] === (saved?.[field] ?? null),
    )

interface LicenseOverviewFormProps {
    /** The saved license; without one (Add License) nothing is saved and the overview is handed to onSaved as typed */
    campaignId?: string
    initialValues?: Partial<LicenseOverview> | null
    onSaved: (overview: LicenseOverview) => void
    onCancel?: () => void
    submitLabel?: string
    /** False inside a dialog, which already frames the form */
    bordered?: boolean
    className?: string
}

/** The hostname of a web address typed with or without a scheme; null when it is not an http(s) URL. */
const hostOf = (value: string): string | null => {
    const text = value.trim()
    if (!text || /\s/.test(text)) return null
    try {
        const url = new URL(/^[a-z][a-z\d+.-]*:/i.test(text) ? text : `https://${text}`)
        if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.')) return null
        return url.hostname.toLowerCase()
    } catch {
        return null
    }
}

/**
 * What a link is for: a social platform's profile is a social link, any other web address is the website; null when it
 * is not a web address.
 */
const linkKindOf = (value: string): LinkKind | null => {
    const host = hostOf(value)
    if (!host) return null
    const social = SOCIAL_HOSTS.some((socialHost) => host === socialHost || host.endsWith(`.${socialHost}`))
    return social ? 'socialUrls' : 'primaryUrl'
}

/**
 * The website or the social link the one link is saved as. A saved link keeps its saved field even when its host would
 * sort it elsewhere; any other link is a social link on a social platform and the website anywhere else.
 */
const linkInputOf = (
    link: string,
    saved?: Partial<LicenseOverview> | null,
): Pick<OverviewInput, 'primaryUrl' | 'socialUrls'> => {
    const kind =
        link === saved?.primaryUrl ? 'primaryUrl' : saved?.socialUrls?.includes(link) ? 'socialUrls' : linkKindOf(link)
    return kind === 'socialUrls' ? { primaryUrl: null, socialUrls: [link] } : { primaryUrl: link, socialUrls: [] }
}

const inputOf = (values: FormValues, saved?: Partial<LicenseOverview> | null): OverviewInput => ({
    ...linkInputOf(values.link.trim(), saved),
    overview: values.overview.trim() || null,
    styleGuideUrl: values.styleGuideUrl.trim() || null,
})

const validateLink = (value: string) => {
    if (!value.trim()) return 'Add your website or a social link.'
    return hostOf(value) ? true : 'Enter a web address, for example https://example.com.'
}

const validateOverview = (value: string) => (value.trim() ? true : 'Enter a quick overview of the business.')

const validateStyleGuideUrl = (value: string) =>
    !value.trim() || hostOf(value) ? true : 'Enter a valid link to the brand style guide.'

const errorFieldOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { field?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.field ?? null
}

const errorCodeOf = (error: unknown): string | null => {
    const graphQLErrors = (error as { graphQLErrors?: { extensions?: { code?: string } }[] })?.graphQLErrors
    return graphQLErrors?.[0]?.extensions?.code ?? null
}

/** The overview was saved somewhere else while this form was open (another tab or a double submit). */
const OVERVIEW_CHANGED = 'LICENSE_OVERVIEW_CHANGED'

/** Saved files of one upload slot to keep, new files to upload on save and the slot's error. */
const useSlotFiles = (slot: FileSlot, saved: LicenseBrandFile[]) => {
    const [kept, setKept] = useState<LicenseBrandFile[]>(saved)
    const [added, setAdded] = useState<File[]>([])
    const [error, setError] = useState<string | null>(null)
    return {
        saved,
        kept,
        added,
        error,
        setError,
        changed: added.length > 0 || kept.length !== saved.length,
        keptIds: kept.length === saved.length ? null : kept.map(({ id }) => id),
        add: (files: File[]) => {
            const message = filesError(slot, kept, [...added, ...files])
            setError(message)
            if (!message) setAdded((current) => [...current, ...files])
        },
        removeKept: (id: string) => {
            setError(null)
            setKept((current) => current.filter((file) => file.id !== id))
        },
        removeAdded: (index: number) => {
            setError(null)
            setAdded((current) => current.filter((_file, position) => position !== index))
        },
    }
}

/**
 * The link, description, style guide link, logo and brand files of a license, which ground AI prompt suggestions. The
 * one required link is the website, or a social profile when the business has no website; a license saved earlier
 * with several links shows its website (or first social link) and keeps only that one when saved. Saving uploads new
 * logo and brand files and reads the link, style guide and brand files on the server to build a brand profile, so it
 * can take a few seconds. Submitting a saved overview without changes skips the save and reports the saved overview
 * straight away. A license that is not saved yet has no logo or brand files and nothing is saved: the overview is saved
 * with the license when it is created.
 */
const LicenseOverviewForm = ({
    campaignId,
    initialValues,
    onSaved,
    onCancel,
    submitLabel = 'Save',
    bordered = true,
    className,
}: LicenseOverviewFormProps) => {
    const [save] = useMutation<{ updateLicenseOverview: LicenseOverview }>(UPDATE_LICENSE_OVERVIEW)
    const brandFiles = useSlotFiles(BRAND_FILES_SLOT, initialValues?.brandFiles ?? [])
    const logo = useSlotFiles(LOGO_SLOT, initialValues?.logo ? [initialValues.logo] : [])
    const slots = { brandFiles, logoFiles: logo }
    const slotList = Object.values(slots)
    const {
        register,
        control,
        handleSubmit,
        watch,
        setError,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        defaultValues: {
            link: initialValues?.primaryUrl ?? initialValues?.socialUrls?.[0] ?? '',
            overview: initialValues?.overview ?? '',
            styleGuideUrl: initialValues?.styleGuideUrl ?? '',
        },
    })
    const overviewLength = watch('overview')?.length ?? 0

    const onSubmit = async (values: FormValues) => {
        clearErrors('root')
        slotList.forEach((slot) => slot.setError(null))
        const input = inputOf(values, initialValues)
        if (!campaignId) {
            onSaved({
                ...input,
                brandFiles: [],
                logo: null,
                brandProfileStatus: null,
                complete: true,
            })
            return
        }
        const filesChanged = slotList.some((slot) => slot.changed)
        if (isUnchanged(input, filesChanged, initialValues)) {
            onSaved(initialValues)
            return
        }
        try {
            const { data } = await save({
                variables: {
                    campaignId,
                    input: {
                        ...input,
                        // Null keeps every saved file, including any added elsewhere since this form opened
                        brandFileIds: brandFiles.keptIds,
                        newBrandFiles: brandFiles.added,
                        keepLogo: logo.saved.length > 0 && logo.kept.length === 0 ? false : null,
                        newLogo: logo.added[0] ?? null,
                    },
                },
            })
            if (data?.updateLicenseOverview) onSaved(data.updateLicenseOverview)
        } catch (error) {
            const field = errorFieldOf(error)
            const message = (error as Error)?.message || 'We could not save the overview. Please try again.'
            if (errorCodeOf(error) === OVERVIEW_CHANGED) setError('root.server', { message })
            else if (field && field in slots) slots[field as keyof typeof slots].setError(message)
            else if (field && LINK_FIELDS.includes(field)) setError('link', { message })
            else if (field && FIELD_NAMES.includes(field)) setError(field as keyof FormValues, { message })
            else setError('root.server', { message: 'We could not save the overview. Please try again.' })
        }
    }

    return (
        <form
            aria-label="License overview"
            noValidate
            // Edit License renders this form inside the license form. Chrome stops a nested form's submit at the
            // enclosing form, before React's root listener sees it, so it is handled in the capture phase; stopping
            // it there also keeps the license form from being saved.
            onSubmitCapture={(event) => {
                event.stopPropagation()
                return handleSubmit(onSubmit)(event)
            }}
            className={cn(
                'tw:flex tw:flex-col tw:gap-3',
                bordered && 'tw:rounded-md tw:border tw:border-gray-600 tw:p-3',
                className,
            )}
        >
            <p className="tw:text-sm tw:text-gray-300 tw:m-0">
                Tell us about the business so the suggestions match its website or social media and brand.
            </p>

            <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="license-overview-link">Website or social link *</Label>
                <Input
                    id="license-overview-link"
                    type="url"
                    placeholder="https://example.com"
                    aria-invalid={Boolean(errors.link)}
                    aria-describedby={errors.link ? `${LINK_HINT_ID} ${LINK_ERROR_ID}` : LINK_HINT_ID}
                    {...register('link', { validate: validateLink })}
                />
                <span id={LINK_HINT_ID} className="tw:text-xs tw:text-gray-400">
                    Your website, or a social profile such as Instagram, Facebook, TikTok or LinkedIn if you have no
                    website. We read it to learn about the business.
                </span>
                {errors.link && (
                    <p id={LINK_ERROR_ID} role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                        {errors.link.message}
                    </p>
                )}
            </div>

            <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="license-overview-overview">Quick overview *</Label>
                <Controller
                    name="overview"
                    control={control}
                    rules={{
                        validate: validateOverview,
                        maxLength: {
                            value: OVERVIEW_MAX_LENGTH,
                            message: `Keep the overview to ${OVERVIEW_MAX_LENGTH} characters or fewer.`,
                        },
                    }}
                    render={({ field }) => (
                        <Textarea
                            id="license-overview-overview"
                            rows={4}
                            maxLength={OVERVIEW_MAX_LENGTH}
                            placeholder="What the business sells, who its customers are and what makes it different."
                            aria-invalid={Boolean(errors.overview)}
                            aria-describedby="license-overview-overview-count"
                            className="tw:h-auto tw:min-h-24 tw:pr-3"
                            name={field.name}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
                <span id="license-overview-overview-count" className="tw:text-xs tw:text-gray-400 tw:self-end">
                    {overviewLength}/{OVERVIEW_MAX_LENGTH}
                </span>
                {errors.overview && (
                    <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                        {errors.overview.message}
                    </p>
                )}
            </div>

            <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="license-overview-style-guide-url">Brand style guide link</Label>
                <Input
                    id="license-overview-style-guide-url"
                    type="url"
                    placeholder="https://example.com/brand-guidelines.pdf"
                    aria-invalid={Boolean(errors.styleGuideUrl)}
                    aria-describedby="license-overview-style-guide-hint"
                    {...register('styleGuideUrl', { validate: validateStyleGuideUrl })}
                />
                <span id="license-overview-style-guide-hint" className="tw:text-xs tw:text-gray-400">
                    Used to pick the brand colours, typography, title and logo position.
                </span>
                {errors.styleGuideUrl && (
                    <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                        {errors.styleGuideUrl.message}
                    </p>
                )}
            </div>

            {campaignId ? (
                <>
                    <BrandFilesField
                        id="license-overview-logo"
                        slot={LOGO_SLOT}
                        kept={logo.kept}
                        added={logo.added}
                        error={logo.error}
                        disabled={isSubmitting}
                        onAdd={logo.add}
                        onRemoveKept={logo.removeKept}
                        onRemoveAdded={logo.removeAdded}
                    />
                    <BrandFilesField
                        id="license-overview-brand-files"
                        kept={brandFiles.kept}
                        added={brandFiles.added}
                        error={brandFiles.error}
                        disabled={isSubmitting}
                        onAdd={brandFiles.add}
                        onRemoveKept={brandFiles.removeKept}
                        onRemoveAdded={brandFiles.removeAdded}
                    />
                </>
            ) : (
                <p className="tw:text-xs tw:text-gray-400 tw:m-0">
                    You can add a logo, images and style guides after the license is created.
                </p>
            )}

            {errors.root?.server && (
                <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                    {errors.root.server.message}
                </p>
            )}

            <div className="tw:flex tw:items-center tw:justify-end tw:gap-2">
                {onCancel && (
                    <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                )}
                <Button type="submit" size="sm" disabled={isSubmitting}>
                    {isSubmitting && <LoaderCircle size={16} className="tw:animate-spin" />}
                    {isSubmitting
                        ? slotList.some((slot) => slot.added.length > 0)
                            ? 'Uploading files…'
                            : 'Reading your link…'
                        : submitLabel}
                </Button>
            </div>
        </form>
    )
}

export default LicenseOverviewForm
